import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import React, { useState } from 'react';
import { List } from 'react-admin';
import { client } from '../services/api-client';
import { isAdmin, isGss, isSuperAdmin, showToast } from '../utils';

/** Matches API package-sell allowlist (Studio City, Huntsville, Sarasota, San Mateo). */
const PACKAGE_SELL_LOCATION_IDS = [1, 21, 22, 23];

const addonPayload = {
  deactivate_missing: true,
};

const packagePayload = {
  deactivate_missing: true,
  location_ids: PACKAGE_SELL_LOCATION_IDS,
};

const statBoxStyle = {
  textAlign: 'center',
  padding: '12px 16px',
  minWidth: 96,
  backgroundColor: '#f5f7fa',
  borderRadius: 8,
  border: '1px solid #e8ecf1',
};

function StatBlock({ label, value }) {
  return (
    <Box style={statBoxStyle}>
      <Typography variant="caption" color="textSecondary" display="block">
        {label}
      </Typography>
      <Typography
        variant="h6"
        component="span"
        style={{ color: '#1976d2', fontWeight: 600 }}
      >
        {value != null ? value : '—'}
      </Typography>
    </Box>
  );
}

function truncateId(id) {
  if (id == null || typeof id !== 'string') return '—';
  if (id.length <= 20) return id;
  return `${id.slice(0, 8)}…${id.slice(-6)}`;
}

function LocationResultCard({ row, index, createdLabel, createdKey }) {
  const hasError = Boolean(row.error);
  const title =
    row.location_id != null
      ? `Location #${row.location_id}`
      : `Location ${index + 1}`;

  return (
    <Paper
      elevation={0}
      style={{
        marginTop: 16,
        padding: 20,
        border: `1px solid ${hasError ? '#ffcdd2' : '#e0e0e0'}`,
        borderRadius: 8,
        backgroundColor: hasError ? '#fff8f8' : '#fff',
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        style={{ gap: 12 }}
      >
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          style={{ gap: 8 }}
        >
          {hasError ? (
            <ErrorOutlineIcon style={{ color: '#d32f2f', fontSize: 28 }} />
          ) : (
            <CheckCircleIcon style={{ color: '#2e7d32', fontSize: 28 }} />
          )}
          <Box>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              component="div"
              title={row.center_id}
              style={{ fontFamily: 'monospace', letterSpacing: 0.3 }}
            >
              Center: {truncateId(row.center_id)}
            </Typography>
          </Box>
        </Box>
        <Typography
          variant="overline"
          style={{
            color: hasError ? '#c62828' : '#2e7d32',
            fontWeight: 700,
            letterSpacing: 1,
          }}
        >
          {hasError ? 'FAILED' : 'OK'}
        </Typography>
      </Box>

      <Box
        display="flex"
        flexWrap="wrap"
        style={{ marginTop: 16, gap: 12, rowGap: 12 }}
      >
        <StatBlock label="Fetched" value={row.fetched} />
        <StatBlock label="Upserted" value={row.upserted} />
        <StatBlock label={createdLabel} value={row[createdKey]} />
      </Box>

      {hasError && (
        <Box
          style={{
            marginTop: 16,
            padding: '12px 14px',
            backgroundColor: '#ffebee',
            borderRadius: 6,
            borderLeft: '4px solid #e53935',
          }}
        >
          <Typography variant="body2" style={{ color: '#b71c1c' }}>
            {row.error}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function normalizeLocationRows(data) {
  if (data == null || typeof data !== 'object') return null;
  if (Array.isArray(data.locations)) return data.locations;
  if (Array.isArray(data)) return data;
  if (data.location_id != null || data.center_id != null) {
    return [data];
  }
  return null;
}

function SyncResultSummary({ data, createdLabel, createdKey }) {
  if (data == null || typeof data !== 'object') return null;

  const locations = normalizeLocationRows(data);
  const message = typeof data.message === 'string' ? data.message : null;

  const knownKeys = new Set([
    'message',
    'locations',
    'items',
    'count',
    'success',
    'location_id',
    'center_id',
    'fetched',
    'upserted',
    createdKey,
    'error',
  ]);
  const extraEntries = Object.entries(data).filter(
    ([k, v]) => !knownKeys.has(k) && v != null && typeof v !== 'object',
  );

  return (
    <Box style={{ marginTop: 24 }}>
      <Typography
        variant="subtitle2"
        color="textSecondary"
        style={{
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          marginBottom: 8,
        }}
      >
        Sync results
      </Typography>

      {message && (
        <Paper
          elevation={0}
          style={{
            padding: '14px 18px',
            marginBottom: 8,
            backgroundColor: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: 8,
          }}
        >
          <Typography variant="body2" style={{ color: '#0d47a1' }}>
            {message}
          </Typography>
        </Paper>
      )}

      {locations && locations.length > 0 ? (
        locations.map((row, i) => (
          <LocationResultCard
            key={row.location_id ?? row.center_id ?? i}
            row={row}
            index={i}
            createdLabel={createdLabel}
            createdKey={createdKey}
          />
        ))
      ) : !locations && !message ? (
        <Typography variant="body2" color="textSecondary">
          No per-location details were returned.
        </Typography>
      ) : null}

      {locations && locations.length === 0 && (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 8 }}
        >
          No locations were processed.
        </Typography>
      )}

      {extraEntries.length > 0 && (
        <Box style={{ marginTop: 16 }}>
          <Divider style={{ marginBottom: 12 }} />
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            gutterBottom
          >
            Additional fields
          </Typography>
          {extraEntries.map(([key, val]) => (
            <Typography key={key} variant="body2" style={{ marginBottom: 4 }}>
              <strong>{key}:</strong> {String(val)}
            </Typography>
          ))}
        </Box>
      )}

      {!locations && extraEntries.length === 0 && !message && (
        <Typography variant="body2" color="textSecondary">
          Sync finished. See response in network tab if details are missing.
        </Typography>
      )}
    </Box>
  );
}

function SyncPanel({
  title,
  description,
  busy,
  onSync,
  summary,
  createdLabel,
  createdKey,
}) {
  return (
    <Card style={{ margin: '1em' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {description}
        </Typography>
        <Button
          color="primary"
          variant="contained"
          disabled={busy}
          onClick={onSync}
          startIcon={
            busy ? <CircularProgress color="inherit" size={20} /> : undefined
          }
        >
          {busy ? 'Syncing…' : 'Sync now'}
        </Button>
        {summary != null && (
          <SyncResultSummary
            data={summary}
            createdLabel={createdLabel}
            createdKey={createdKey}
          />
        )}
      </CardContent>
    </Card>
  );
}

export const ZenotiSyncList = ({ permissions, ...props }) => {
  const [addonBusy, setAddonBusy] = useState(false);
  const [addonSummary, setAddonSummary] = useState(null);
  const [packageBusy, setPackageBusy] = useState(false);
  const [packageSummary, setPackageSummary] = useState(null);

  const canSyncAddons = isAdmin(permissions) || isSuperAdmin(permissions);
  const canSyncPackages =
    isAdmin(permissions) || isSuperAdmin(permissions) || isGss(permissions);

  const handleAddonSync = async () => {
    setAddonBusy(true);
    setAddonSummary(null);
    try {
      const resp = await client('admin/sync-addons', {
        authNeeded: true,
        method: 'POST',
        data: addonPayload,
      });
      setAddonSummary(resp);
      showToast(resp.message || 'Add-on sync completed', 'success');
    } catch (err) {
      const msg =
        (err && (err.message || err.error)) ||
        (typeof err === 'string' ? err : 'Add-on sync failed');
      showToast(String(msg), 'error');
    } finally {
      setAddonBusy(false);
    }
  };

  const handlePackageSync = async () => {
    setPackageBusy(true);
    setPackageSummary(null);
    try {
      const resp = await client('admin/sync-packages', {
        authNeeded: true,
        method: 'POST',
        data: packagePayload,
      });
      setPackageSummary(resp);
      showToast(resp.message || 'Package sync completed', 'success');
    } catch (err) {
      const msg =
        (err && (err.message || err.error)) ||
        (typeof err === 'string' ? err : 'Package sync failed');
      showToast(String(msg), 'error');
    } finally {
      setPackageBusy(false);
    }
  };

  return (
    <List
      {...props}
      title="Zenoti sync"
      empty={false}
      pagination={false}
      actions={false}
      bulkActionButtons={false}
      exporter={false}
    >
      <Box>
        {canSyncAddons && (
          <SyncPanel
            title="Zenoti add-on catalog"
            description="Fetches add-ons from Zenoti for each active location and upserts the local catalog. When deactivate_missing is on, Zenoti-linked rows no longer returned from Zenoti can be hidden from the guest catalog."
            busy={addonBusy}
            onSync={handleAddonSync}
            summary={addonSummary}
            createdLabel="Created services"
            createdKey="created_addon_services"
          />
        )}
        {canSyncPackages && (
          <SyncPanel
            title="Zenoti package catalog (Secret Menu)"
            description="Pulls packages from Zenoti for package-enabled studios (Studio City, Huntsville, Sarasota, San Mateo) and updates the local catalog used by /packages. Inactive or missing Zenoti packs can be hidden when deactivate_missing is on. Packs need an active price in Zenoti to appear on the guest menu."
            busy={packageBusy}
            onSync={handlePackageSync}
            summary={packageSummary}
            createdLabel="Created packs"
            createdKey="created_packages"
          />
        )}
      </Box>
    </List>
  );
};
